import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import {
  Identifier,
  UrlInjectionMap,
  UrlRouteMap,
} from "@shared/services/api/types";
import { AuthProfileSerializer, ChangeEmailPayload } from "./serializer-types";

export class AuthProfileRepository extends Repository<AuthProfileSerializer> {
  public readonly repositoryName = "AuthProfileRepository";

  public readonly endpoint = "api/auth/profile";

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      retrieve: "/",
      emailChange: `/{id}/email/change`,
    };
  }

  /**
   * Override the default retrieve method to add source_channel and source_referer
   */
  public async retrieve(
    id?: Identifier,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<AuthProfileSerializer> {
    const conf: ExtendedAxiosRequestConfig = config ?? {};

    const injects: UrlInjectionMap = {
      [this.primaryKey]: id ?? this.injects[this.primaryKey],
    };

    // If this request comes from the discovery pages, set the source channel and referer
    // for our internal analytics to track the session.
    if (conf && typeof window !== "undefined" && window.location) {
      const url = new URL(window.location.href);
      const source_referer =
        url.searchParams.get("source_referer") || undefined;

      if (url.pathname.startsWith("/discover/") || url.pathname === "/") {
        conf.params = {
          ...conf.params,
          source_channel: "discovery",
          source_referer: window.location.href,
        };
      } else if (source_referer) {
        conf.params = {
          ...conf.params,
          source_channel: "non_discovery",
          source_referer,
        };
      }
    }

    const { data } = await this.request(this.buildUrl("retrieve", injects), {
      method: "get",
      showToastOnError: false,
      ...config,
    });

    return data;
  }

  public async emailChange(
    payload: ChangeEmailPayload
  ): Promise<{ detail: string }> {
    const { data } = await this.request<{ detail: string }>(
      this.buildUrl("emailChange", { id: payload.id }),
      {
        method: "POST",
        data: payload,
      }
    );
    return data;
  }
}
